import { AxiosError, AxiosResponse } from 'axios'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query'

import * as requests from './requests'
import {
  TOpportunitiesV3Details,
  TOpportunitiesV3SupportListParams,
  TOpportunitiesV3UnlinkRequestPayload,
  TOpportunitiesV3OverdueContractItem,
  TOpportunitiesV3ApnefItem,
  TOpportunitiesV3PartnershipItem,
  TOpportunitiesV3ListItem,
  TOpportunitiesV3ListParams,
  TOpportunitiesV3EvaluationPayload,
  TOpportunitiesV3FiltersResponse,
  TOpportunitiesV3SummaryApiResponse,
  TOpportunitiesV3RatesHistoryResponse
} from './types'
import { ResponseError, ResponseSuccess } from '../../types'

export const KEY_OPPORTUNITIES_FILTERS = 'getOpportunitiesV3Filters'

export const KEY_OPPORTUNITIES_LIST = 'getOpportunitiesV3List'

export const KEY_OPPORTUNITIES_DETAILS = 'getOpportunitiesV3Details'

export const KEY_OPPORTUNITIES_SUMMARY = 'getOpportunitiesV3Summary'

export const KEY_OPPORTUNITIES_OVERDUE_CONTRACTS = 'getOpportunitiesV3OverdueContracts'

export const KEY_OPPORTUNITIES_APNEF_LIST = 'getOpportunitiesV3ApnefList'

export const KEY_OPPORTUNITIES_PARTNERSHIP_LIST = 'getOpportunitiesV3PartnershipList'

export const KEY_OPPORTUNITIES_V3_RATES_HISTORY = 'getOpportunitiesV3RatesHistory'

export const useGetOpportunitiesV3FiltersQuery = () =>
  useQuery<
    AxiosResponse<ResponseSuccess<TOpportunitiesV3FiltersResponse>>,
    AxiosError<ResponseError>
  >([KEY_OPPORTUNITIES_FILTERS], () => requests.getOpportunitiesFilters())

export const useGetOpportunitiesV3ListInfiniteQuery = (
  params: TOpportunitiesV3ListParams,
  enabled?: boolean
) =>
  useInfiniteQuery<
    AxiosResponse<ResponseSuccess<TOpportunitiesV3ListItem[]>>,
    AxiosError<ResponseError>
  >(
    [KEY_OPPORTUNITIES_LIST, params],
    ({ pageParam }) => requests.getOpportunitiesList({ ...params, page: pageParam }),
    {
      getNextPageParam: (lastPage) =>
        lastPage.data.metadata.pagination.current_page < lastPage.data.metadata.pagination.last_page
          ? lastPage.data.metadata.pagination.current_page + 1
          : false,
      enabled: enabled ?? true
    }
  )

export const useGetOpportunitiesV3SummaryQuery = (params?: TOpportunitiesV3ListParams) =>
  useQuery<AxiosResponse<TOpportunitiesV3SummaryApiResponse>, AxiosError<ResponseError>>(
    [KEY_OPPORTUNITIES_SUMMARY, params],
    () => requests.getOpportunitiesSummary(params)
  )

export const useGetOpportunitiesV3DetailsQuery = (id: string, enabled?: boolean) =>
  useQuery<AxiosResponse<ResponseSuccess<TOpportunitiesV3Details>>, AxiosError<ResponseError>>(
    [KEY_OPPORTUNITIES_DETAILS, id],
    () => requests.getOpportunitiesDetails(id),
    {
      enabled: enabled ?? true
    }
  )

export const useGetOpportunitiesV3RatesHistoryQuery = (id?: string, enabled = true) =>
  useQuery<
    AxiosResponse<ResponseSuccess<TOpportunitiesV3RatesHistoryResponse>>,
    AxiosError<ResponseError>
  >(
    [`${KEY_OPPORTUNITIES_V3_RATES_HISTORY}.${id}`],
    () => requests.getOpportunitiesV3RatesHistory(id),
    {
      enabled: Boolean(id) && enabled
    }
  )

export const useCreateOpportunitiesUnlinkRequestMutation = () =>
  useMutation<
    AxiosResponse<ResponseSuccess<Record<string, unknown>>>,
    AxiosError<ResponseError>,
    TOpportunitiesV3UnlinkRequestPayload
  >(requests.createOpportunitiesUnlinkRequest)

export const useCreateOpportunitiesEvaluationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<ResponseSuccess<Record<string, unknown>>>,
    AxiosError<ResponseError>,
    TOpportunitiesV3EvaluationPayload
  >(requests.createOpportunitiesEvaluation, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        `${KEY_OPPORTUNITIES_V3_RATES_HISTORY}.${variables.opportunity_id}`
      ])
    }
  })
}

export const useGetOpportunitiesV3OverdueContractsInfiniteQuery = (
  params: TOpportunitiesV3SupportListParams,
  enabled?: boolean
) =>
  useInfiniteQuery<
    AxiosResponse<ResponseSuccess<TOpportunitiesV3OverdueContractItem[]>>,
    AxiosError<ResponseError>
  >(
    [KEY_OPPORTUNITIES_OVERDUE_CONTRACTS, params],
    ({ pageParam }) => requests.getOpportunitiesOverdueContracts({ ...params, page: pageParam }),
    {
      getNextPageParam: (lastPage) =>
        lastPage.data.metadata.pagination.current_page < lastPage.data.metadata.pagination.last_page
          ? lastPage.data.metadata.pagination.current_page + 1
          : false,
      enabled: enabled ?? true
    }
  )

export const useGetOpportunitiesV3ApnefInfiniteQuery = (
  params: TOpportunitiesV3SupportListParams,
  enabled?: boolean
) =>
  useInfiniteQuery<
    AxiosResponse<ResponseSuccess<TOpportunitiesV3ApnefItem[]>>,
    AxiosError<ResponseError>
  >(
    [KEY_OPPORTUNITIES_APNEF_LIST, params],
    ({ pageParam }) => requests.getOpportunitiesApnefList({ ...params, page: pageParam }),
    {
      getNextPageParam: (lastPage) =>
        lastPage.data.metadata.pagination.current_page < lastPage.data.metadata.pagination.last_page
          ? lastPage.data.metadata.pagination.current_page + 1
          : false,
      enabled: enabled ?? true
    }
  )

export const useGetOpportunitiesV3PartnershipInfiniteQuery = (
  params: TOpportunitiesV3SupportListParams,
  enabled?: boolean
) =>
  useInfiniteQuery<
    AxiosResponse<ResponseSuccess<TOpportunitiesV3PartnershipItem[]>>,
    AxiosError<ResponseError>
  >(
    [KEY_OPPORTUNITIES_PARTNERSHIP_LIST, params],
    ({ pageParam }) => requests.getOpportunitiesPartnershipList({ ...params, page: pageParam }),
    {
      getNextPageParam: (lastPage) =>
        lastPage.data.metadata.pagination.current_page < lastPage.data.metadata.pagination.last_page
          ? lastPage.data.metadata.pagination.current_page + 1
          : false,
      enabled: enabled ?? true
    }
  )
